import React, { useEffect, useState } from 'react';
import { Modal, Spin, Alert, Typography, Tag, Button } from 'antd';
import { ReloadOutlined } from '@ant-design/icons';

import { portalService } from '../../services/api';
import { buildApiErrorMessage } from '../../utils/apiError';

const { Title, Text } = Typography;

// Contrato real: GET /api/portal/avaliacoes/{id}
// (AvaliacaoResponsavelDetalheResponse) -- somente leitura, o backend
// nunca devolve isto para avaliacao PENDENTE/RASCUNHO ou fora do escopo
// do responsavel (404 identico para os tres casos). Terminologia
// amigavel so no label visual -- o valor cru do nivel nunca aparece.
const NIVEL_INFO = {
  EM_DESENVOLVIMENTO: { label: 'Em desenvolvimento', color: '#d48806' },
  ADEQUADO: { label: 'Adequado', color: '#1677ff' },
  BOM: { label: 'Bom', color: '#389e0d' },
  AVANCA_ALEM_DO_ESPERADO: { label: 'Avança além do esperado', color: '#722ed1' },
};

// Ordem institucional fixa dos grupos (Fase 11) -- mesma ordem usada no
// Portal do Professor. Um grupo sem nenhum critério aplicável a esta
// turma simplesmente não aparece.
const ORDEM_GRUPOS = ['PARTICIPACAO', 'COMPORTAMENTO', 'FREQUENCIA', 'DESENVOLVIMENTO_TECNICO'];
const GRUPO_LABEL = {
  PARTICIPACAO: 'Participação',
  COMPORTAMENTO: 'Comportamento',
  FREQUENCIA: 'Frequência',
  DESENVOLVIMENTO_TECNICO: 'Desenvolvimento Técnico',
};

const agruparCriteriosPorGrupo = (criterios) => {
  const porGrupo = new Map();
  criterios.forEach((c) => {
    if (!porGrupo.has(c.grupo)) porGrupo.set(c.grupo, []);
    porGrupo.get(c.grupo).push(c);
  });
  return ORDEM_GRUPOS.filter((g) => porGrupo.has(g)).map((g) => ({ grupo: g, itens: porGrupo.get(g) }));
};

const MENSAGEM_NAO_ENCONTRADA = 'Esta avaliação não está disponível.';

const AvaliacaoResponsavelModal = ({ open, avaliacaoId, onClose }) => {
  const [detalhe, setDetalhe] = useState(null);
  const [loading, setLoading] = useState(false);
  const [erro, setErro] = useState(null); // { status, message } | null

  const carregar = async () => {
    if (!avaliacaoId) return;
    setLoading(true);
    setErro(null);
    try {
      const response = await portalService.getAvaliacao(avaliacaoId);
      setDetalhe(response.data);
    } catch (err) {
      const status = err?.response?.status;
      if (status === 404) {
        setErro({ status, message: MENSAGEM_NAO_ENCONTRADA });
      } else {
        const { message: mensagem } = buildApiErrorMessage(err, 'Não foi possível carregar esta avaliação.');
        setErro({ status, message: mensagem });
      }
      setDetalhe(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) {
      carregar();
    } else {
      setDetalhe(null);
      setErro(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, avaliacaoId]);

  return (
    <Modal
      title={detalhe ? detalhe.nome_periodo : 'Avaliação'}
      open={open}
      onCancel={onClose}
      footer={<Button onClick={onClose}>Fechar</Button>}
      width={480}
    >
      {loading && (
        <div style={{ display: 'flex', justifyContent: 'center', padding: 32 }}>
          <Spin />
        </div>
      )}

      {!loading && erro && (
        <Alert
          type={erro.status === 404 ? 'warning' : 'error'}
          showIcon
          message="Não foi possível abrir"
          description={erro.message}
          action={
            erro.status !== 404 ? (
              <Button size="small" icon={<ReloadOutlined />} onClick={carregar}>
                Tentar novamente
              </Button>
            ) : undefined
          }
        />
      )}

      {!loading && !erro && detalhe && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <Text type="secondary">
            {detalhe.nome_turma} • {detalhe.nome_modalidade}
          </Text>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            {agruparCriteriosPorGrupo(detalhe.criterios).map(({ grupo, itens }) => (
              <div key={grupo} style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                <Text strong type="secondary">
                  {GRUPO_LABEL[grupo] || grupo}
                </Text>
                {itens.map((criterio, indice) => {
                  const nivelInfo = NIVEL_INFO[criterio.nivel] || {
                    label: criterio.nivel || 'Sem resposta',
                    color: 'default',
                  };
                  return (
                    <div key={`${criterio.nome}-${indice}`} style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                      <Text strong>{criterio.nome}</Text>
                      {criterio.descricao && (
                        <Text type="secondary" style={{ fontSize: 12 }}>
                          {criterio.descricao}
                        </Text>
                      )}
                      <Tag color={nivelInfo.color} style={{ width: 'fit-content' }}>
                        {nivelInfo.label}
                      </Tag>
                    </div>
                  );
                })}
              </div>
            ))}
          </div>

          {detalhe.comentario && detalhe.comentario.trim() !== '' && (
            <div>
              <Title level={5} style={{ marginBottom: 4 }}>
                Observações sobre a evolução
              </Title>
              <Text>{detalhe.comentario}</Text>
            </div>
          )}
        </div>
      )}
    </Modal>
  );
};

export default AvaliacaoResponsavelModal;
